import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import UISpinner from 'Components/UIComponents/UISpinner';
import { useDownloadFiles } from 'Hooks/Common';
import { useDocumentDownload, useGetDocumentByHash } from 'Hooks/Document';
import {
  Document,
  DocumentDownloadPayload,
  DocumentDownloadTypes,
  DocumentStatuses,
  Signer,
} from 'Interfaces/Document';
import { AuthorizedRoutePaths } from 'Interfaces/RoutePaths';
import {
  documentSeparatedFileKeyExtractorForDocument,
  documentSeparatedFileKeyExtractorForDocumentActivities,
} from 'Pages/Documents/extractors/DocumentSeparatedFileKeyExtractor';
import Toast from 'Services/Toast';

import { isNotEmpty } from '../../Utils/functions';

type PageParams = {
  documentId: Document['id'];
  signerId?: Signer['id'];
};

export const DocumentDownload = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<PageParams>();

  const [documentByHash, setDocumentByHash] = useState<Document>();

  const [downloadDocument, isDownloading, isReady] = useDocumentDownload();
  const [getDocumentByHash] = useGetDocumentByHash();

  const navigateToRoot = useCallback(() => {
    navigate(AuthorizedRoutePaths.BASE_PATH, { replace: true });
  }, [navigate]);

  const handleDocumentDownload = useCallback(
    async (payload: DocumentDownloadPayload) => {
      try {
        await downloadDocument(payload);
      } catch (err) {
        Toast.handleErrors(err, { toastId: 'download_error' });
      } finally {
        navigate(AuthorizedRoutePaths.BASE_PATH, { replace: true });
      }
    },
    [downloadDocument, navigate],
  );

  const { hash, documentId, signerId } = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const { documentId, signerId } = params || {};

    return {
      hash: searchParams.get('hash'),
      documentId,
      signerId,
    };
  }, [location.search, params]);

  const [downloadSeparatedDocument, isDownloadingDocuments] = useDownloadFiles<Document>({
    fileExtractors: [
      documentSeparatedFileKeyExtractorForDocument,
      documentSeparatedFileKeyExtractorForDocumentActivities,
    ],
    hash: hash,
    name: documentByHash?.title,
  });

  const handleSeparatedDocumentDownload = useCallback(
    async (doc: Document) => {
      try {
        if ((doc?.id, doc?.resultDocumentPdfFileKey)) {
          await downloadSeparatedDocument([doc]);
        }
      } catch (err) {
        Toast.handleErrors(err, { toastId: 'separated_download_error' });
      } finally {
        navigateToRoot();
      }
    },
    [downloadSeparatedDocument, navigateToRoot],
  );

  const handleDownload = useCallback(async () => {
    try {
      if (hash && documentId && isReady) {
        const doc = await getDocumentByHash({
          documentId,
          hash,
        });
        //@ts-ignore
        setDocumentByHash(doc);
      }
    } catch (err) {
      Toast.handleErrors(err, { toastId: 'download_by_hash_error' });
      navigateToRoot();
    }
  }, [hash, documentId, isReady, getDocumentByHash, navigateToRoot]);

  useEffect(() => {
    handleDownload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash, documentId, isReady]);

  useEffect(() => {
    if (hash && documentId && isReady && documentByHash) {
      if (
        isNotEmpty(documentByHash) &&
        (documentByHash?.downloadType === DocumentDownloadTypes.MERGED ||
          documentByHash?.status !== DocumentStatuses.COMPLETED)
      ) {
        handleDocumentDownload({
          hash,
          documentId,
          signerId,
        });
      } else {
        //@ts-ignore
        handleSeparatedDocumentDownload(documentByHash);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentByHash]);

  return (
    <>
      {isDownloadingDocuments && (
        <UISpinner width={50} height={50} wrapperClassName="spinner--main__wrapper" />
      )}
    </>
  );
};
